import clsx from "clsx";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import RichTextEditor from "react-rte";
import * as helpers from "../../../helpers";
import * as components from "./components";

const htmlLabel = "html";
const richTextLabel = "rich text";
const previewLabel = "preview";
const tabOptions = [richTextLabel, htmlLabel, previewLabel];

const defaultUri = "your-uri-goes-here";

export default function NewsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  // SET DEFAULT STATE //
  const [missingField, setMissingField] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [article, setArticleObj] = useState(undefined);

  const [title, onTitle] = useState(undefined);
  const [author, onAuthor] = useState(undefined);
  const [date, onDate] = useState(undefined);
  const [uri, onUriChange] = useState(defaultUri);

  const [summary, onSummary] = useState(undefined);
  const [imgURL, onImgURL] = useState(undefined);
  const [imgALT, onAlt] = useState(undefined);
  const [tags, onTagsChange] = useState([]);

  const [richText, onRichTextChange] = useState(
    RichTextEditor.createEmptyValue()
  );
  const text = richText.toString("html");

  const [textDisplay, onTextBodyChange] = useState(richTextLabel);
  const [htmlText, onHTMLChange] = useState(undefined);

  // CHANGE STATE IF EDITING ARTICLE //
  useEffect(() => {
    if (id) {
      helpers.getArticle(id).then((obj) => {
        const date = helpers.formatDatePicker(obj.publish_date);
        const body = RichTextEditor.createValueFromString(obj.body, "html");
        const tags = obj.product_tags.concat(obj.article_tags);
        const newObj = { ...obj, tags };

        setArticleObj(newObj);
        onTitle(obj.title);
        onAuthor(obj.author);
        onUriChange(obj.uri);
        onDate(date);
        onImgURL(obj.image_url);
        onAlt(obj.image_credits);
        onRichTextChange(body);
        onSummary(obj.summary);
        onTagsChange(tags);
      });
    }
  }, [id]);

  // CHANGE STATE IF HTML EDITING VIEW //
  useEffect(() => {
    onHTMLChange(richText.toString("html"));
  }, [richText]);

  // CHANGE STATE ON SAVE - SUCCESS OR ERROR //
  useEffect(() => {
    if (confirmMsg) {
      setTimeout(() => setConfirmMsg(false), 2400);
    }
    if (errorMsg) {
      setTimeout(() => setErrorMsg(""), 2400);
    }
    if (missingField) {
      setTimeout(() => setMissingField(false), 2400);
    }
  }, [missingField, confirmMsg, errorMsg]);

  // MANAGE INPUT FIELD CHANGES //
  const onUri = (e) => {
    const newUri = e.target.value.substring(1);
    onUriChange(newUri);
  };

  const onTags = (selectedItem) => {
    const duplicate = tags.includes(selectedItem.be);
    let tagsArr;

    if (duplicate) {
      tagsArr = tags.filter((existingItem) => existingItem !== selectedItem.be);
    } else {
      tagsArr = [...tags, selectedItem.be];
    }

    onTagsChange(tagsArr);
  };

  const onHTML = (e) => {
    const newRichText = RichTextEditor.createValueFromString(
      e.target.value,
      "html"
    );
    onRichTextChange(newRichText);
    onHTMLChange(e.target.value);
  };

  // CREATE AN ARTICLE //
  const postRequest = () => {
    let convertedDate = undefined;

    if (date) {
      convertedDate = new Date(date).toISOString();
    }

    const body = {
      title,
      publish_date: convertedDate,
      uri,
      image_url: imgURL,
      image_credits: imgALT,
      body: text,
      summary,
      tags,
    };

    helpers.createArticle(body).then((resp) => {
      if (resp.message.includes("Success")) {
        setConfirmMsg(true);
      } else {
        setErrorMsg(resp.message);
      }
    });
  };

  // EDIT AN ARTICLE //
  const patchRequest = () => {
    let body;

    if (id) {
      if (article?.title !== title) {
        body = { ...body, title };
      }
      if (article?.author !== author) {
        body = { ...body, author };
      }
      if (date) {
        const convertedDate = new Date(date).toISOString();
        if (article?.publish_date !== convertedDate) {
          body = { ...body, publish_date: convertedDate };
        }
      }
      if (article?.uri !== uri) {
        body = { ...body, uri };
      }
      if (article?.image_url !== imgURL) {
        body = { ...body, image_url: imgURL };
      }
      if (article?.image_credits !== imgALT) {
        body = { ...body, image_credits: imgALT };
      }
      if (article?.body !== text) {
        body = { ...body, body: text };
      }
      if (article?.summary !== summary) {
        body = { ...body, summary };
      }

      const tagsDontChange =
        article?.tags.length === tags.length &&
        article?.tags.every((tag) => tags.includes(tag));

      if (!tagsDontChange) {
        body = { ...body, tags };
      }

      if (body) {
        return helpers.editArticle(id, body);
      }
    }
  };

  // SAVE AND EXIT BUTTONS //
  const onSaveStayClick = () => {
    if (article) {
      patchRequest().then(() => setConfirmMsg(true));
    }
    const notDefaultUri = uri !== defaultUri;
    if (title && notDefaultUri && date && !article) {
      postRequest();
    } else {
      return setMissingField(true);
    }
  };
  const onExitClick = () => navigate(-1);
  const useSaveExitClick = () => {
    onSaveStayClick();
    onExitClick();
  };

  // COMPONENT PROPS //
  const aduProps = { uri, onUri, date, onDate, author, onAuthor };
  const prevImgProps = { imgURL, onImgURL, imgALT, onAlt };
  const sumProps = { summary, onSummary };
  const tagsProps = { onTags, tags };

  const tabProps = { textDisplay, onTextBodyChange, tabOptions };
  const htmlProps = { htmlText, onHTML };
  const previewProps = { tags, title, date, body: text, author };
  const saveBtnProps = {
    onSaveStayClick,
    useSaveExitClick,
    onExitClick,
    missingField,
    errorMsg,
    confirmMsg,
  };

  return (
    <div className={parentContainerStyle}>
      <div className="w-4/5">
        {/* header edit options */}
        <div className="flex w-full items-center">
          <div className={labelStyle}>Title</div>
          <input
            className={inputStyle}
            type="title"
            value={title}
            placeholder={title || "Article Title"}
            onChange={(e) => onTitle(e.target.value)}
          />
        </div>
        <components.AuthorDateURI {...aduProps} />

        {/* sidebar edit options */}
        <div className="flex flex-row w-full mt-4">
          <div className="flex flex-col mr-10 w-1/3">
            <components.PreviewImg {...prevImgProps} />
            <components.Summary {...sumProps} />
            <components.Tags {...tagsProps} />
          </div>

          {/* primary text body */}
          <div className="w-2/3">
            <components.Tabs {...tabProps} />
            {textDisplay === richTextLabel && (
              <RichTextEditor
                value={richText}
                onChange={onRichTextChange}
                className={textBodyStyle}
              />
            )}
            {textDisplay === htmlLabel && (
              <components.HTMLTextArea {...htmlProps} />
            )}
            {textDisplay === previewLabel && (
              <components.ArticlePreview {...previewProps} />
            )}
            <components.SaveButtons {...saveBtnProps} />
          </div>
        </div>
      </div>
    </div>
  );
}

const parentContainerStyle = clsx(
  "w-full flex justify-center items-center mt-[50px]"
);

export const textBodyStyle = clsx(
  "max-w-[800px] w-full h-[640px] overflow-y-scroll "
);
export const inputStyle = clsx(
  "p-2 h-10 border-b-2 text-sm focus:outline-none cursor-pointer w-full"
);
export const labelStyle =
  "first:mt-0 font-bold text-xs p-2 bg-black text-white tracking-wider uppercase";
